<div class="driver-deviations-container">
  <div class="page-header">
    <h1>Föraravvikelser</h1>
  </div>

  <!-- Search & Filter Section -->
  <div class="search-section">
    <div class="search-controls">
      <div class="date-picker-container">
        <label for="trafficDate">Trafikdygn:</label>
        <app-datetime-field
          inputId="trafficDate"
          mode="date"
          [value]="selectedDate"
          (valueChange)="selectedDate = $event"
          placeholder="Välj datum">
        </app-datetime-field>
      </div>

      <app-chip-filter-field
        label="Tågnummer:"
        placeholder="Sök tågnummer..."
        [options]="filters.state['train'].filtered"
        [selected]="filters.state['train'].selected"
        [disabled]="trainDeviations.length === 0"
        (inputChange)="filters.onInput('train', $event); applyFilters()"
        (optionSelected)="filters.select('train', $event); applyFilters()"
        (removed)="filters.remove('train', $event); applyFilters()">
      </app-chip-filter-field>

      <app-chip-filter-field
        label="Förare:"
        placeholder="Sök förare..."
        [options]="filters.state['driver'].filtered"
        [selected]="filters.state['driver'].selected"
        [disabled]="trainDeviations.length === 0"
        (inputChange)="filters.onInput('driver', $event); applyFilters()"
        (optionSelected)="filters.select('driver', $event); applyFilters()"
        (removed)="filters.remove('driver', $event); applyFilters()">
      </app-chip-filter-field>

      <app-chip-filter-field
        label="Sträcka:"
        placeholder="Sök sträcka..."
        [options]="filters.state['route'].filtered"
        [selected]="filters.state['route'].selected"
        [disabled]="trainDeviations.length === 0"
        (inputChange)="filters.onInput('route', $event); applyFilters()"
        (optionSelected)="filters.select('route', $event); applyFilters()"
        (removed)="filters.remove('route', $event); applyFilters()">
      </app-chip-filter-field>

      <div class="spacer"></div>
    </div>

    <div class="search-actions">
      <button
        mat-raised-button
        color="primary"
        class="search-button"
        (click)="loadData()"
        [disabled]="loading">
        @if (!loading) {
          <mat-icon>search</mat-icon>
        }
        @if (loading) {
          <mat-icon class="spinner">refresh</mat-icon>
        }
        {{ loading ? 'Hämtar...' : 'Hämta' }}
      </button>
      @if (filters.hasAnySelected()) {
        <button
          mat-stroked-button
          (click)="clearFilters()"
          class="clear-filters-btn"
          >
          <mat-icon>clear</mat-icon>
          Rensa filter
        </button>
      }
    </div>
  </div>

  <!-- Tabs Section -->
  <div class="tabs-section">
    <div class="tabs">
      <button
        class="tab-button"
        [class.active]="activeTab === 'new'"
        (click)="onTabChange('new')">
        Nya konflikter <span class="tab-count">({{ getNewCount() }})</span>
      </button>
      <button
        class="tab-button"
        [class.active]="activeTab === 'handled'"
        (click)="onTabChange('handled')">
        Hanterade <span class="tab-count">({{ getHandledCount() }})</span>
      </button>
    </div>
  </div>

  <!-- Results Section -->
  <div class="results-section">
    <!-- No results when no data at all -->
    @if (trainDeviations.length === 0 && !loading) {
      <div class="no-results">
        <mat-icon>info</mat-icon>
        <p>Inga föraravvikelser hittades för valt datum.</p>
      </div>
    }

    <!-- No results when filtered out -->
    @if (trainDeviations.length > 0 && filteredDeviations.length === 0 && !loading) {
      <div class="no-results">
        <mat-icon>filter_alt_off</mat-icon>
        @if (activeTab === 'new') {
          <p>Inga nya föraravvikelser hittades.</p>
        }
        @if (activeTab === 'handled') {
          <p>Inga hanterade föraravvikelser hittades.</p>
        }
      </div>
    }

    <!-- Deviations Table -->
    @if (filteredDeviations.length > 0) {
      <div class="deviations-table-container">
        <table class="deviations-table">
          <thead>
            <tr>
              <th class="col-train sortable" (click)="sortBy('train')">
                <span>Tåg <mat-icon class="sort-icon">{{ getSortIcon('train') }}</mat-icon></span>
              </th>
              <th class="col-route-location">Sträcka</th>
              <th class="col-route-time sortable" (click)="sortBy('time')">
                <span>Tid <mat-icon class="sort-icon">{{ getSortIcon('time') }}</mat-icon></span>
              </th>
              <th class="col-status">Status</th>
              <th class="col-driver">Aktiv förare</th>
              <th class="col-comment">Kommentar</th>
              <th class="col-updated">Senast uppdaterad</th>
            </tr>
          </thead>
          <tbody>
            @for (deviation of paginatedDeviations; track deviation) {
              <!-- Main row -->
              <tr class="deviation-row"
                [class.handled]="deviation.isHandled"
                (click)="openResolveDialog(deviation)"
                role="button"
                [attr.aria-label]="'Hantera avvikelse för tåg ' + deviation.trainNumber">
                <td class="col-train">
                  <strong [innerHTML]="deviation.trainNumber | highlight: filters.state['train'].inputValue"></strong>
                </td>
                <td class="col-route-location">
                  <span [innerHTML]="(deviation.route.origin + ' → ' + deviation.route.destination) | highlight: filters.state['route'].inputValue"></span>
                </td>
                <td class="col-route-time">
                  {{ formatTime(deviation.route.departureAtUtc) }} – {{ formatTime(deviation.route.arrivalAtUtc) }}
                </td>
                <td class="col-status">
                  <span class="status-badge" [ngClass]="getStatusClass(deviation.status)">
                    {{ getStatusLabel(deviation.status) }}
                  </span>
                </td>
                <td class="col-driver">
                  @for (group of deviation.conflictGroups; track group; let lastGroup = $last) {
                    @for (driver of getDisplayedGroupDrivers(group, deviation.isHandled); track driver) {
                      <div class="driver-info-compact">
                        <div class="driver-line-top">
                          <span class="driver-name" [innerHTML]="(driver.firstName + ' ' + driver.lastName) | highlight: filters.state['driver'].inputValue"></span>
                          @if (driver.route) {
                            <span class="driver-route">
                              {{ driver.route.origin }} → {{ driver.route.destination }}
                            </span>
                          }
                        </div>
                        <div class="driver-line-bottom">
                          @if (driver.phoneNumber) {
                            <span
                              class="phone-link"
                              (click)="copyPhoneNumber(driver.phoneNumber, $event)"
                              matTooltip="Klicka för att kopiera"
                              role="button"
                              tabindex="0">
                              {{ driver.phoneNumber }}
                            </span>
                          }
                          @if (driver.role !== 'PLANNED' || driver.isSelected) {
                            <div class="driver-meta">
                              <span class="role-tag" [ngClass]="getRoleClass(driver.role)">
                                {{ getRoleLabel(driver.role) }}
                              </span>
                              @if (driver.isSelected) {
                                <mat-icon class="selected-icon" matTooltip="Vald förare">check_circle</mat-icon>
                              }
                            </div>
                          }
                        </div>
                      </div>
                    }
                    @if (!lastGroup) {
                      <div class="conflict-group-divider"></div>
                    }
                  }
                </td>
                <td class="col-comment">
                  <span class="comment-preview">{{ deviation.note || '-' }}</span>
                </td>
                <td class="col-updated">
                  {{ deviation.lastUpdated ? formatDateTime(deviation.lastUpdated) : '-' }}
                </td>
              </tr>
            }
          </tbody>
        </table>
        <!-- Pagination -->
        <div class="table-footer">
          <mat-paginator
            class="dark-results-paginator"
            [length]="filteredDeviations.length"
            [pageSize]="pageSize"
            [pageIndex]="pageIndex"
            [pageSizeOptions]="pageSizeOptions"
            (page)="onPageChange($event)"
            showFirstLastButtons>
          </mat-paginator>
        </div>
      </div>
    }
  </div>

  <!-- Loading State -->
  @if (loading) {
    <div class="loading-state">
      <mat-spinner></mat-spinner>
      <p>Hämtar avvikelser...</p>
    </div>
  }
</div>

